import React, { useEffect, useMemo, useState } from "react";
import { FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import axios from "axios";
import { toast } from "react-toastify";
import apiKey from "../../../config/apiKey";
import { useTranslation } from "react-i18next";

/* ---------- Small UI helpers ---------- */
const SkeletonRow = () => (
  <tr className="animate-pulse">
    {[...Array(4)].map((_, i) => (
      <td key={i} className="py-4 px-2">
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </td>
    ))}
  </tr>
);

const DemoClass = () => {
  const { t } = useTranslation();
  const admin = JSON.parse(localStorage.getItem("edo-admin"));

  /* ---------- Local state ---------- */
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  // Add/Edit form (top form is for add; modal uses same shape for edit)
  const [currentTask, setCurrentTask] = useState({ days: "", duration: "" });

  // Edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditTaskId(null);
    setCurrentTask({ days: "", duration: "" });
  };

  /* ---------- Fetch ---------- */
  const fetchData = async () => {
    setLoading(true);
    setFetchError("");
    try {
      const res = await axios.get(`${apiKey}/api/edolutions/admin/demoduration`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      });
      setTasks(res.data || []);
    } catch (error) {
      setFetchError(t("adminDash.demoClass.fetchError"));
      toast.error(t("adminDash.demoClass.fetchError"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.token]);

  /* ---------- Create ---------- */
  const handleSubmit = async () => {
    if (!currentTask.days.trim() || !currentTask.duration.trim()) {
      toast.error(t("adminDash.demoClass.fillBothFields"), {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
      });
      return;
    }
    try {
      const res = await axios.post(
        `${apiKey}/api/edolutions/admin/demoduration`,
        currentTask,
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );
      toast.success(res.data?.message || t("adminDash.demoClass.addedSuccess"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });
      setCurrentTask({ days: "", duration: "" });
      fetchData();
    } catch {
      toast.error(t("adminDash.demoClass.addError"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  /* ---------- Update ---------- */
  const handleUpdate = async () => {
    if (!currentTask.days.trim() || !currentTask.duration.trim()) {
      toast.error(t("adminDash.demoClass.fillBothFields"), {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
      });
      return;
    }
    try {
      const res = await axios.put(
        `${apiKey}/api/edolutions/admin/demoduration/${editTaskId}`,
        currentTask,
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );
      toast.success(res.data?.message || t("adminDash.demoClass.updatedSuccess"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });
      fetchData();
      closeModal();
    } catch {
      toast.error(t("adminDash.demoClass.updateError"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  /* ---------- Delete ---------- */
  const handleDelete = async (id) => {
    const ok = window.confirm(t("adminDash.demoClass.confirmDelete"));
    if (!ok) return;

    try {
      const res = await axios.delete(
        `${apiKey}/api/edolutions/admin/demoduration/${id}`,
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );
      toast.success(res.data?.message || t("adminDash.demoClass.deletedSuccess"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });
      // Optimistic update
      setTasks((prev) => prev.filter((task) => task.id !== id));
    } catch {
      toast.error(t("adminDash.demoClass.deleteError"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  /* ---------- Search (debounced) ---------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredTasks = useMemo(() => {
    const q = debounced.toLowerCase();
    if (!q) return tasks;
    return tasks.filter(
      (task) =>
        (task.days || "").toLowerCase().includes(q) ||
        (task.duration || "").toLowerCase().includes(q)
    );
  }, [tasks, debounced]);

  /* ---------- Pagination ---------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const totalItems = filteredTasks.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = Math.min((currentPage - 1) * itemsPerPage, totalItems);
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const pageSlice = useMemo(
    () => filteredTasks.slice(startIndex, endIndex),
    [filteredTasks, startIndex, endIndex]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, debounced]);

  // Windowed buttons (max 5)
  const pageRange = useMemo(() => {
    const maxBtns = 5;
    if (totalPages <= maxBtns) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - (maxBtns - 1)));
    return Array.from({ length: maxBtns }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  return (
    <div className="relative my-8 max-sm:max-w-sm mx-auto sm:w-full flex flex-col min-w-0 mb-6 shadow-sm bg-white rounded-2xl border border-gray-200">
      {/* Header */}
      <div className="px-6 pt-6">
        <p className="text-xl font-bold">{t("adminDash.demoClass.title")}</p>
      </div>

      {/* Add form */}
      <div className="px-6 mt-4 flex flex-wrap gap-2 items-end">
        <label className="block">
          <span className="sr-only">{t("adminDash.demoClass.daysLabel")}</span>
          <input
            type="text"
            className="border p-2 shadow-sm w-64 rounded-md outline-none"
            placeholder={t("adminDash.demoClass.daysPlaceholder")}
            value={currentTask.days}
            onChange={(e) => setCurrentTask((task) => ({ ...task, days: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="sr-only">{t("adminDash.demoClass.durationLabel")}</span>
          <input
            type="text"
            className="border p-2 shadow-sm w-64 rounded-md outline-none"
            placeholder={t("adminDash.demoClass.durationPlaceholder")}
            value={currentTask.duration}
            onChange={(e) => setCurrentTask((task) => ({ ...task, duration: e.target.value }))}
          />
        </label>
        {!editTaskId ? (
          <button
            className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B] text-sm font-semibold rounded-md text-white px-5 py-2"
            onClick={handleSubmit}
          >
            {t("adminDash.common.submit")}
          </button>
        ) : (
          <button
            className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] text-sm font-semibold rounded-md text-white px-5 py-2"
            onClick={handleUpdate}
          >
            {t("adminDash.common.update")}
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="px-6 mt-4 flex flex-wrap items-center justify-between">
        <p className="text-lg font-semibold">{t("adminDash.demoClass.listTitle")}</p>
        <div className="relative border rounded-md flex items-center px-4 py-2 shadow-sm">
          <FiSearch className="h-5 w-5 absolute left-2 text-gray-500" />
          <input
            type="text"
            className="outline-none pl-7 text-sm"
            placeholder={t("adminDash.demoClass.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="mx-6 my-4 overflow-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-50 text-[#6b7280] sticky top-0 z-10">
            <tr>
              <th className="py-3 px-2 font-medium w-1/12">{t("adminDash.common.sr")}</th>
              <th className="py-3 px-2 font-medium">{t("adminDash.demoClass.daysLabel")}</th>
              <th className="py-3 px-2 font-medium">{t("adminDash.demoClass.durationLabel")}</th>
              <th className="py-3 px-2 font-medium text-center w-2/12">{t("adminDash.common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y text-center">
            {loading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {!loading && pageSlice.length === 0 && (
              <tr>
                <td colSpan={4} className="py-8 px-2 text-gray-500">
                  {fetchError || t("adminDash.demoClass.noData")}
                </td>
              </tr>
            )}

            {!loading &&
              pageSlice.map((task, idx) => (
                <tr className="hover:bg-gray-50" key={task.id}>
                  <td className="py-4 px-2">{startIndex + idx + 1}</td>
                  <td className="py-4 px-2 text-left">{task.days}</td>
                  <td className="py-4 px-2 text-left">{task.duration}</td>
                  <td className="py-4 px-2">
                    <div className="flex justify-center gap-6">
                      <button
                        className="text-[#2A3A54] hover:opacity-80"
                        onClick={() => {
                          setCurrentTask({ days: task.days, duration: task.duration });
                          setEditTaskId(task.id);
                          setIsModalOpen(true);
                        }}
                        title={t("adminDash.common.edit")}
                      >
                        <FaRegEdit className="text-lg" />
                      </button>
                      <button
                        className="text-rose-600 hover:text-rose-700"
                        onClick={() => handleDelete(task.id)}
                        title={t("adminDash.common.delete")}
                      >
                        <FaRegTrashAlt className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap gap-5 justify-between items-center w-full text-sm px-6 pb-6">
        <div className="flex items-center gap-2 text-gray-600">
          <select
            className="text-sm border rounded-lg px-2 py-1 bg-white"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
          >
            {[10, 20, 30, 40, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>{t("adminDash.common.showing")}</span>
          <span className="font-medium text-gray-800">
            {totalItems === 0 ? 0 : startIndex + 1}-{endIndex}
          </span>
          <span>{t("adminDash.common.of")} {totalItems} {t("adminDash.common.entries")}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label={t("adminDash.common.previousPage")}
          >
            <MdKeyboardArrowLeft />
          </button>

          {pageRange.map((p) => (
            <button
              key={p}
              className={`text-base rounded-lg px-3 py-1 ${
                p === currentPage ? "text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" : "text-black bg-gray-100"
              }`}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}

          <span>{t("adminDash.common.of")} {totalPages} {t("adminDash.common.pages")}</span>

          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label={t("adminDash.common.nextPage")}
          >
            <MdKeyboardArrowRight />
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="px-5 py-4 border-b">
              <h2 className="text-lg font-semibold">{t("adminDash.demoClass.updateTitle")}</h2>
            </div>
            <div className="px-5 py-5">
              <div className="mb-4">
                <label className="block text-sm text-gray-700 mb-1">{t("adminDash.demoClass.daysLabel")}</label>
                <input
                  type="text"
                  className="border p-2 w-full rounded-md shadow-sm"
                  value={currentTask.days}
                  onChange={(e) => setCurrentTask((task) => ({ ...task, days: e.target.value }))}
                />
              </div>
              <div className="mb-2">
                <label className="block text-sm text-gray-700 mb-1">{t("adminDash.demoClass.durationLabel")}</label>
                <input
                  type="text"
                  className="border p-2 w-full rounded-md shadow-sm"
                  value={currentTask.duration}
                  onChange={(e) =>
                    setCurrentTask((task) => ({ ...task, duration: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.cancel")}
              </button>
              <button
                onClick={handleUpdate}
                className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:opacity-90 text-white px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.update")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoClass;
